// Static-PLY hair renderer — orchestrator class.
//
// State + lifecycle + public API live here. The heavy lifting is in the
// sibling modules:
//   - HairData.js   all per-Gaussian buffers + setMaterial/setSpecular/…
//                   verbs; owned as `this.data`. The other modules operate
//                   on a HairData instance, never reach into HairRenderer.
//   - splatUtil.js  packed-splat read/write helpers
//   - geom.js       view-direction math
//   - matMod.js     mat_mod output → per-Gaussian albedo + f_dec
//   - specMod.js    view_enc + spec_mod → normals + spec_vis
//   - shade.js      shadeFrame, writeDebugColors, runNeuralBsdfOnce
//   - envInstall.js installVolumeEnv, snapshotProbe (volume light-probe SH)
//   - envDebug.js   3D bounds / centroid overlay
//
// Pipeline (replaces the old geom + appr + mesh_encoder + lift chain).
//
//   init:
//     1. Parallel load: hair_static.ply (a three GaussianSplat), manifest, sidecar,
//        const_latent, ONNX (mat_mod + spec_mod + view_enc + bsdf_mod),
//        baked BRDF FG LUT (required static asset).
//     2. Add the splat to the scene with `visible = flags.enabled` (default false).
//     3. Install the FG-LUT sampler from the fetched LUT.
//     4. Run mat_mod(const_latent) once → albedo[P*3] + f_dec[P*32].
//     5. Pre-build cached f_dec_pad plane (32, s_pad, n_pad) for spec_mod.
//     6. Extract per-Gaussian positions from the geometry (used by shading).
//     7. Install the volume light-probe samplers (read window.__LIGHT_PROBE__'s
//        SH live), snapshot the probe, then run NeuralBSDF once from that SH.
//
//   update(delta):
//     - Skip if disabled, busy, or failed.
//     - On camera move (camera-settle gated): run view_enc + spec_mod
//       (when useSpecMod), recompute normals from base_normal + d_n,
//       call shadeGaussiansEnvmap → setPackedSplatRgb in-place →
//       colour attribute needsUpdate = true.
//     - When the probe SH drifts (avatar moved to a differently-lit spot):
//       re-run NeuralBSDF from the new SH before shading.
//
// Stability:
//   - Try/catch wraps every async block; the body splat is never touched.
//   - useNeuralBsdf and useSpecMod are independent toggles — a bad output
//     from either falls back without re-rendering the whole scene.

import * as THREE from 'three';

import { OnnxRunner } from '../OnnxRunner.js';
import { loadHairPly, loadConstLatent } from '../loadHairPly.js';
import { loadSidecar } from '../loadSidecar.js';
import { makeFgLutSampler } from '../shading.js';
import { loadFgLut } from '../loadFgLut.js';
import { computeEnvCentroidBounds } from './splatUtil.js';
import { matrixDrift, camPosInSplatLocal } from './geom.js';
import { shouldShade } from './shadePolicy.js';
import { createRevealGate, onShadeApplied, onSetEnabled } from './revealGate.js';
import { shadeFrame, writeDebugColors } from './shade.js';
import { installVolumeEnv, snapshotProbe } from './envInstall.js';
import { sh27ToCoeff48, lightProbeSHDrift } from '../../lib/envSamplers.js';
import { buildEnvDebugGroup, disposeEnvDebugGroup } from './envDebug.js';
import { HairData } from './HairData.js';
import { createPerfAgg } from './perfAgg.js';
import { HairGpuShader } from './hairGpuShader.js';
import { MatModUnpacker } from './matModUnpacker.js';
import { activeSpecGrid } from './specGrid.js';

const VIEW_FEAT_DIM = 8; // spec_mod view channels (spec input = 32 f_dec + 8 view)

// Scratch reused across render-loop calls so the hot path doesn't allocate
// per frame. Single-renderer-instance assumption — fine for this app.
const _tmpVec3a = new THREE.Vector3();

// Probe-SH drift threshold (relative). Re-shade on a small change (catches the
// grid finishing its load or __AVATAR_PROBE_POS__ moving while the view is
// still). NeuralBSDF is re-queried with the live SH on every shade.
const RESHADE_SH_DRIFT = 0.01;

export class HairRenderer {
  /**
   * @param {Object} opts
   *   renderer: THREE.WebGLRenderer
   *   scene:    THREE.Scene
   *   camera:   THREE.PerspectiveCamera
   *   envMesh:  the existing environment splat — only used for the debug overlay
   *   assetsBaseUrl: '/assets/hair_static'
   *   flags: { enabled, useNeuralBsdf, useSpecMod }
   *   onStep: optional (name) => void  loading progress
   */
  static async create(opts) {
    const inst = new HairRenderer(opts);
    await inst._init();
    return inst;
  }

  constructor(opts) {
    this.opts = opts;
    this.flags = {
      enabled: false,
      useNeuralBsdf: true,
      useSpecMod: true,
      // Debug view selector. One of:
      //   'shaded'     — final color (default)
      //   'diffuse'    — diffuse term only (albedo × refl × env_diffuse)
      //   'specular'   — specular term only (env_specular × spec_vis)
      //   'albedo'     — raw albedo from mat_mod
      //   'bsdf'       — NeuralBSDF reflectance per Gaussian
      //   'roughness'  — roughness as greyscale
      //   'normal'     — current normal (base_normal + d_n) encoded RGB
      debugMode: 'shaded',
      ...(opts.flags || {}),
    };
    this.runner = null;
    this.splatMesh = null;
    this._colourAttr = null;
    this._colourWords = null;
    this.manifest = null;
    this.brdfLut = null;

    // Samplers + SH state populated by installVolumeEnv / snapshotProbe.
    // `_sh27` is the live SH snapshot the samplers read; `shCoeff48` is its
    // degree-3 re-layout fed to NeuralBSDF; `_shLastShade` is the SH the last
    // shade used (drift against it forces a re-shade on a still view).
    this.fgLutSampler = null;
    this.diffuseSampler = null;
    this.specularSampler = null;
    this.shCoeff48 = null;
    this._sh27 = null;
    this._shLastShade = new Float32Array(27);
    // SH the NeuralBSDF reflectance was last computed from. NeuralBSDF depends
    // only on lighting (SH) + static f_dec — NOT the camera — so it must re-run
    // on SH drift, not on every (view-driven) re-shade. Tracked separately from
    // `_shLastShade` so slow drift accumulates against the last actual BSDF run.
    this._shLastBsdf = new Float32Array(27);

    // All per-Gaussian state (dims + buffers + centroid) lives on a
    // HairData instance. Built in `_init` once the manifest and sidecar
    // are loaded; sibling modules (matMod, specMod, shade, envInstall)
    // read and write through this directly.
    this.data = null;

    // Frame loop state. Owned by update() / setFlag().
    this._lastCameraMatrix = new THREE.Matrix4();
    this._prevTickMatrix = null;
    this._lastCameraMoveAt = 0;
    this._lastShadeAt = 0;
    this._frameTimes = [];
    this._busy = false;
    // Set when a shade was requested (drift or a flag toggle) but the pipeline
    // was still in flight, so update() re-fires it once the pipeline drains —
    // a serialized shade must never be silently dropped.
    this._reshadeRequested = false;
    this._failed = false;

    // Reveal gate: the splat is added to the scene HIDDEN and only shown once its
    // first valid color shade is in the packed array (see revealGate.js). Kills
    // the gray-flash where Spark rendered the raw, unshaded PLY colors during the
    // async init + first-readback window. `wantVisible` is set from flags.enabled
    // in _init and tracks the caller's enabled intent thereafter.
    this._revealGate = createRevealGate(false);

    // Per-stage shade timing — always on (the brackets are a few
    // performance.now() calls, negligible next to the shade). Drives
    // window.__HAIR_PERF__ + the [hair][perf] breakdown line.
    this.perfAgg = createPerfAgg();
  }

  async _init() {
    const { assetsBaseUrl, scene, camera, envMesh, renderer } = this.opts;

    // 1. Parallel load all assets + ONNX init.
    const runner = new OnnxRunner({ baseUrl: assetsBaseUrl });
    const [{ mesh, manifest }, sidecar, constLatent, , fgLut] = await Promise.all([
      loadHairPly(assetsBaseUrl).then((r) => { this.opts.onStep?.('hairMesh'); return r; }),
      loadSidecar(assetsBaseUrl),
      loadConstLatent(assetsBaseUrl),
      runner.init().then(() => { this.opts.onStep?.('hairOnnx'); }),
      // Universal Karis env-BRDF FG LUT — fetched from the baked static asset in
      // parallel (was a ~2.8 s synchronous CPU bake here). The asset is required:
      // a missing LUT rejects and fails hair init loudly — no compute fallback.
      // See src/hair/loadFgLut.js.
      loadFgLut(),
    ]);
    this.runner = runner;
    this.splatMesh = mesh;
    this.manifest = manifest;

    // 2. Build HairData — owns dims + every per-Gaussian buffer (the
    // sidecar warning about P mismatch lives in the constructor too).
    this.data = new HairData(manifest, sidecar);
    const data = this.data;

    // Loud load-time count so a regression to the wrong (e.g. 2.3M Myra)
    // bundle is obvious — the isaac hair must be the ~549k isaac_fused_5k set.
    console.info(`[hair] loaded P=${data.P} (S=${data.S}×N=${data.N}, pad ${data.sPad}×${data.nPad}, feat_dim=${manifest.feat_dim}) from ${assetsBaseUrl}`);

    // Add the splat to the scene but keep it HIDDEN until the first valid shade
    // is applied (the reveal gate) — otherwise the raw, unshaded PLY colours
    // (neutral gray) are on screen through the async mat_mod → bsdf → shade init
    // and the first GPU-readback window. `wantVisible` carries the caller's
    // enabled intent.
    this._revealGate.wantVisible = this.flags.enabled;
    this.splatMesh.visible = false;
    scene.add(this.splatMesh);

    // Per-Gaussian positions, once, from the geometry's own f32 centres.
    data.setPositionsFromGeometry(this.splatMesh.geometry);
    // The colour attribute as u32 words — the shade's readback merges straight
    // into these (see splatUtil's header on why the layouts coincide). Held for
    // the session: the array is the geometry's own, so writing it and flagging
    // the attribute is the whole upload path.
    this._colourAttr = this.splatMesh.geometry.getAttribute('color');
    this._colourWords = new Uint32Array(this._colourAttr.array.buffer,
                                        this._colourAttr.array.byteOffset,
                                        this._colourAttr.count);
    console.info('[hair] splat centroid (cm):', data.centroid.toArray());

    // Compute env splat centroid + bounds. The loft env was captured from a
    // viewpoint roughly inside the room, so its centroid is the natural
    // probe origin — that's where all 6 cube faces see the room walls.
    // Probing at the head (which sits outside the loft volume in this scene)
    // gives a one-sided capture.
    this._envCentroid = new THREE.Vector3();
    this._envMin = new THREE.Vector3();
    this._envMax = new THREE.Vector3();
    this._envFarHint = 1000; // cm, default
    {
      const eb = computeEnvCentroidBounds(this.opts.envMesh);
      if (eb) {
        this._envCentroid.copy(eb.envCentroid);
        this._envMin.copy(eb.envMin);
        this._envMax.copy(eb.envMax);
        this._envFarHint = eb.envFarHint;
        console.info('[hair] env splat centroid (cm):', this._envCentroid.toArray(),
          'bounds:', [eb.envMin.x, eb.envMax.x, eb.envMin.y, eb.envMax.y, eb.envMin.z, eb.envMax.z].map((v) => v.toFixed(1)),
          'far hint:', this._envFarHint.toFixed(1));
      }
    }
    this._envDebug = null;

    // 3. BRDF FG LUT for split-sum specular — resolved from the baked static
    //    asset in the parallel-load block above (loadFgLut), not computed here.
    this.brdfLut = fgLut;
    this.fgLutSampler = makeFgLutSampler(this.brdfLut);

    // 4. Run mat_mod ONCE — const_latent is a fixed nn.Parameter, so output is static.
    const matRes = await this.runner.runMatMod(constLatent);
    if (matRes.gpuBuffer) {
      // GPU-resident unpack: matUv stays on the GPU; a WGSL pass fans it into the
      // shade/spec/bsdf buffers (no ~280 MB readback, no CPU crop/permute).
      await this._unpackMatModGpu(matRes, data);
    } else {
      data.setMaterial(matRes);   // CPU unpack (wasm mat_mod / gpuShade off)
    }

    // 5. Initial normals + identity reflectance are already set up by the
    // HairData constructor; nothing to do here.

    // 6. Env light setup. The diffuse + specular samplers read the volume
    //    light-probe SH (window.__LIGHT_PROBE__, installed by LightProbeVolume)
    //    live — the same source the ?debug_env=1 oracle ball shades from.
    installVolumeEnv(this);
    snapshotProbe(this);
    this.opts.onStep?.('hairProbe');

    // Wait (bounded) for the volume light probe before the first shade so the
    // initial NeuralBSDF + shade run against REAL lighting, not zero SH. Without
    // this the first shade can land unlit and only self-heal on a later
    // probe/drift — exactly the "gray until I move the camera" symptom. A
    // genuinely missing grid (no-cache env) times out and we shade anyway (loud),
    // never hang; update()'s SH-drift re-shade still self-heals if it shows later.
    await this._waitForProbe();

    // NeuralBSDF input is the probe SH (degree-3 re-layout) — run it once here
    // so the first shade uses the trained reflectance, not identity. If the
    // probe grid isn't loaded yet, _sh27 is zeros and reflectance stays ~0;
    // update() re-runs it once the grid resolves and the SH drifts.
    await this._runNeuralBsdfOnce();

    // 7. Initial shade. Samplers + neuralBsdfReflectance are populated; camera
    //    move triggers a re-shade via update().
    await this._shadeFrame(camera, /*runSpecMod*/ false);
    this.opts.onStep?.('hairShade');

    // CPU shade writes colors into the packed array inline, so the splat is
    // ready to reveal NOW. The GPU-shade path only DISPATCHED here — its colors
    // land later via update()'s pollApply, which reveals there. Either way: the
    // splat is never shown before it's colored, so there's no gray frame.
    if (!this.runner?.gpuShade) this._reveal();
  }

  /** GPU-resident mat_mod unpack. Pre-creates the three consumer buffers (the
   *  GPU-shade static-attr buffer, the resident spec_mod input, the resident
   *  bsdf features), dispatches the WGSL unpack to fill them straight from
   *  mat_mod's gpu-buffer output, marks the spec/bsdf inputs resident, and frees
   *  the ORT output once the pass completes. No CPU albedo/f_dec is produced. */
  async _unpackMatModGpu(matRes, data) {
    const runner = this.runner;
    const device = runner.gpuDevice();
    if (!device) { // shouldn't happen (gpuShade implies a device) — CPU fallback
      const matUv = new Float32Array(await matRes.ortOut.getData());
      try { matRes.ortOut.dispose?.(); } catch { /* ignore */ }
      data.setMaterial({ matUv, dims: matRes.dims });
      return;
    }
    const cIn = 32 + VIEW_FEAT_DIM;
    const planeSrc = data.sPad * data.nPad; // mat_mod's full output grid
    // spec_mod's input grid — the tight active grid when the pack's spec_mod takes
    // dynamic dims, else the same padded grid. The unpack re-lays f_dec from src to
    // dst, so this is the ONLY place the two grids meet. Must match the grid
    // specMod.js feeds per frame or the resident buffer's dims check throws.
    const { sG, nG } = activeSpecGrid(data, runner.specCropActive);
    const planeDst = sG * nG;

    // The GPU-shade static buffer must exist first so the unpack can write its
    // albedo lanes. pos/bn/rough are filled from CPU here; albedo comes from the
    // unpack. The rest of the shader (pipeline etc.) builds lazily on first shade.
    if (!this._gpuShader) this._gpuShader = new HairGpuShader();
    this._gpuShader.initStatic(device, data, /*albedoFromData*/ false);

    const bStatic = this._gpuShader.staticBuffer();
    const bSpecIn = runner.ensureSpecInputBuffer(cIn, sG, nG);
    const bBsdfFeat = runner.ensureBsdfFeatBuffer(data.P);

    if (!this._matUnpacker) this._matUnpacker = new MatModUnpacker();
    this._matUnpacker.unpack(device, matRes.gpuBuffer, { bStatic, bBsdfFeat, bSpecIn },
      { P: data.P, N: data.N, nPadSrc: data.nPad, planeSrc, nPadDst: nG, planeDst });

    // The f_dec channels are now resident → the first spec_mod / bsdf run skips
    // its cold upload and only tiles the per-frame view planes.
    runner.markSpecStaticReady();
    runner.markBsdfFeatReady();

    // The CPU staging buffers (~600 MB: specIn + fDecPad) are never used on this
    // path — the resident buffer holds f_dec now. Drop them so they don't pin
    // heap until specMod.js's first-shade free would (it also guards on this).
    data.specIn = null;
    data.fDecPad = null;

    // Free the 280 MB ORT output once the unpack GPU work completes (destroying
    // it earlier would invalidate the buffer the dispatch still references).
    const ortOut = matRes.ortOut;
    device.queue.onSubmittedWorkDone().then(() => { try { ortOut.dispose?.(); } catch { /* ignore */ } });
  }

  /** Run view_enc + spec_mod, refreshing per-Gaussian normals + spec_vis on
   *  the HairData. We own the camera→splat-local conversion since we know
   *  the splat matrix; the actual ONNX call lives in HairData.setSpecular. */
  async _runSpecMod(camera) {
    const camPos = camPosInSplatLocal(camera, this.splatMesh, _tmpVec3a);
    await this.data.setSpecular(this.runner, camPos);
  }

  /** Single shading pass: compute per-Gaussian RGB and write into packed array.
   *  Doesn't touch envProbe or NeuralBSDF caches. */
  async _shadeFrame(camera, useSpecModForThisFrame) {
    await shadeFrame(this, camera, useSpecModForThisFrame, (cam) => this._runSpecMod(cam));
  }

  /** Snapshot the live volume probe, re-layout its SH into the degree-3
   *  shCoeff48 the NeuralBSDF expects, and run it → per-Gaussian reflectance. */
  async _runNeuralBsdfOnce() {
    snapshotProbe(this);
    sh27ToCoeff48(this._sh27, this.shCoeff48);
    const ok = await this.data.setNeuralReflectance(this.runner, this.shCoeff48);
    // Record the SH this reflectance was computed from, so update() can skip
    // re-running NeuralBSDF until the lighting actually drifts (not per-rotation).
    // A FAILED run (identity fallback) is deliberately NOT recorded — leaving
    // _shLastBsdf stale keeps bsdfDrift high so the next shade retries instead
    // of freezing wrong reflectance until the lighting happens to change.
    if (ok) this._shLastBsdf.set(this._sh27);
  }

  /** Reveal the splat once its first valid shade is in the packed array. Flips
   *  visibility to the caller's enabled intent (`wantVisible`); idempotent, so
   *  it's cheap to call on every landed GPU readback. */
  _reveal() {
    const visible = onShadeApplied(this._revealGate);
    if (this.splatMesh) this.splatMesh.visible = visible;
  }

  /** Bounded wait for the volume light probe (window.__LIGHT_PROBE__, installed
   *  by LightProbeVolume once its baked SH grid loads). Resolves true as soon as
   *  it appears, or false after `timeoutMs` (no-cache env / probe never arrives)
   *  — the caller shades anyway; update()'s SH-drift re-shade self-heals if a
   *  probe shows up later. */
  async _waitForProbe(timeoutMs = 2000) {
    if (typeof window === 'undefined') return false;
    if (window.__LIGHT_PROBE__) return true;
    const t0 = performance.now();
    while (performance.now() - t0 < timeoutMs) {
      await new Promise((r) => setTimeout(r, 32));
      if (window.__LIGHT_PROBE__) return true;
    }
    console.warn(`[hair] light probe not ready after ${timeoutMs}ms — revealing with current shading; will self-heal on probe/drift.`);
    return false;
  }

  // Serialized-pipeline gate. True while a shade's async body is running
  // (`_busy`) OR — on the GPU-shade path — a dispatched shade's readback hasn't
  // been applied yet (`gpuShader.pending`, cleared by pollApply). No new shade
  // starts until this clears, so hair runs 1 inference → 1 apply → next with no
  // overlap, mirroring the isaac lift's depth-1 serial gate.
  _shadeInFlight() {
    return this._busy || !!this._gpuShader?.pending;
  }

  update(_delta) {
    if (this._failed || !this.flags.enabled) return;
    // NOTE: no `!visible` guard — pre-reveal the splat is intentionally hidden,
    // but pollApply below must still run so the first landed GPU shade can REVEAL
    // it. Disabled hair already returned above on `!flags.enabled`.
    if (!this.splatMesh) return;

    // Apply a completed async GPU-shade readback (gpushade). Runs every frame,
    // independent of the re-shade throttle AND of the reveal gate. The readback
    // is already u8-packed RGBA words, so this is a straight u32 merge into the
    // mesh's own colour bytes (alpha preserved) — no float repack loop, no
    // mapAsync in the shade path. The FIRST landed readback also reveals the splat.
    // Only MERGE while the 'shaded' view is active — a landed shaded readback
    // must not stomp an inspection view; slots are still consumed either way.
    if (this._gpuShader && this._colourWords) {
      // Reveal is independent of the merge: a landed readback means the first
      // shade is done, so show the splat even while a debug view suppresses the
      // 'shaded' merge (otherwise enabling hair inside a debug view leaves it
      // stuck hidden until the user switches back to 'shaded'). Check landed
      // BEFORE pollApply, which frees the ready slot.
      const landed = this._gpuShader.hasLanded();
      if (this._gpuShader.pollApply(this._colourWords,
        this.flags.debugMode === 'shaded')) {
        this._colourAttr.needsUpdate = true;
      }
      if (landed) this._reveal(); // first landed shade → show the splat
    }

    // Serialized: don't start a new shade until the previous shade's inference
    // AND its readback apply (pollApply, above) have completed. A request that
    // arrives mid-flight is remembered so it fires the moment the pipeline drains.
    if (this._shadeInFlight()) return;
    const { camera } = this.opts;

    // Drift of EITHER the camera or the splat (head-follow) from the pose we
    // last shaded. The splat term is Infinity until the first shade records a
    // baseline, so the first update always shades.
    const camDrift = matrixDrift(this._lastCameraMatrix, camera.matrixWorld);
    const splatDrift = this._lastSplatMatrix
      ? matrixDrift(this._lastSplatMatrix, this.splatMesh.matrix)
      : Infinity;
    const drift = Math.max(camDrift, splatDrift);

    // The volume probe is sampled at the avatar position by LightProbeVolume, so
    // a still view can still need a re-shade — the grid finished loading or
    // __AVATAR_PROBE_POS__ moved. SH drift since the last shade forces one.
    const probe = (typeof window !== 'undefined') ? window.__LIGHT_PROBE__ : null;
    const shDrift = probe ? lightProbeSHDrift(probe, this._shLastShade) : 0;
    // Drift of the lighting since NeuralBSDF last ran (it's view-independent).
    const bsdfDrift = probe ? lightProbeSHDrift(probe, this._shLastBsdf) : 0;

    // Throttle: don't re-shade 60×/s while the camera drifts. shouldShade still
    // guarantees one final shade within ~MIN_INTERVAL_MS of the view settling
    // (drift stays > MOVE_EPS until the resting pose is shaded).
    const now = performance.now();
    const viewSettled = shouldShade({ drift, now, lastShadeAt: this._lastShadeAt });
    if (!this._reshadeRequested && !viewSettled && shDrift < RESHADE_SH_DRIFT) return;
    this._reshadeRequested = false;
    this._lastShadeAt = now;

    this._busy = true;
    const t0 = now;
    (async () => {
      // Re-query NeuralBSDF only when the LIGHTING drifted since it last ran —
      // it's view-independent, so a camera rotation (SH unchanged) must NOT
      // re-run it. This fires on env (re)apply, on the grid finishing its load,
      // and as the avatar moves through the light field — not on every re-shade.
      if (this.flags.useNeuralBsdf && probe && bsdfDrift >= RESHADE_SH_DRIFT) {
        await this._runNeuralBsdfOnce();
      }
      await this._shadeFrame(camera, this.flags.useSpecMod);
      this._shLastShade.set(this._sh27);
      this._lastCameraMatrix.copy(camera.matrixWorld);
      if (!this._lastSplatMatrix) this._lastSplatMatrix = new THREE.Matrix4();
      this._lastSplatMatrix.copy(this.splatMesh.matrix);
      const elapsed = performance.now() - t0;
      await this._recordFrameTime(elapsed);
    })()
      .catch((e) => { console.warn('[hair] update failed:', e); this._failed = true; })
      .finally(() => { this._busy = false; });
  }

  async _recordFrameTime(ms) {
    this._frameTimes.push(ms);
    if (this._frameTimes.length > 3) this._frameTimes.shift();

    if (!this._slowShadeWarned &&
        this._frameTimes.length === 3 &&
        this._frameTimes.every((t) => t > 80)) {
      this._slowShadeWarned = true;
      console.warn(`[hair] update >80 ms 3× — shade is slow (avg=${
        (this._frameTimes.reduce((a, b) => a + b, 0) / 3).toFixed(1)
      } ms). Auto-degrade disabled.`);
    }
  }

  setFlag(name, value) {
    const old = this.flags[name];
    this.flags[name] = value;
    if (name === 'enabled') {
      // Route through the reveal gate: enabling BEFORE the first shade landed
      // must not flash a gray splat — stay hidden and kick a shade (whose apply
      // reveals: update()'s pollApply on the GPU path, inline here on CPU).
      const { visible, needsShade } = onSetEnabled(this._revealGate, value);
      if (this.splatMesh) this.splatMesh.visible = visible;
      if (needsShade && !this._shadeInFlight()) {
        this._busy = true;
        this._shadeFrame(this.opts.camera, this.flags.useSpecMod)
          .then(() => { if (!this.runner?.gpuShade) this._reveal(); })
          .finally(() => { this._busy = false; });
      } else if (needsShade) {
        this._reshadeRequested = true; // pipeline busy — update() re-fires it
      }
    } else if (name === 'debugMode' && old !== value) {
      // CPU shade path: everything was computed in the last shade — rewrite
      // packed colors from the CPU arrays. GPU shade path: the per-term arrays
      // are never populated (mat_mod unpacks straight into gpu-buffers, and the
      // WGSL shade writes packed colors directly), so returning to 'shaded'
      // forces a fresh shade instead of packing stale zeros. On the GPU path the
      // data views are limited: 'roughness' is valid (from the sidecar), 'normal'
      // shows the base normal (spec_mod's d_n stays on-GPU), and albedo/diffuse/
      // specular read zero CPU arrays → black. Use ?ep=wasm (CPU shade) for the
      // full set of per-term debug views.
      if (this.runner?.gpuShade && value === 'shaded') {
        if (!this._shadeInFlight()) {
          this._busy = true;
          this._shadeFrame(this.opts.camera, this.flags.useSpecMod)
            .finally(() => { this._busy = false; });
        } else {
          this._reshadeRequested = true; // pipeline busy — update() re-fires it
        }
      } else if (this.splatMesh && this.data?.outColors) {
        writeDebugColors(this.splatMesh, value, this.data);
      }
    } else if (name === 'useNeuralBsdf' && old !== value) {
      if (!this._shadeInFlight()) {
        this._busy = true;
        this._shadeFrame(this.opts.camera, this.flags.useSpecMod)
          .finally(() => { this._busy = false; });
      } else {
        this._reshadeRequested = true; // pipeline busy — update() re-fires it
      }
    } else if (name === 'useSpecMod' && old !== value) {
      if (!this._shadeInFlight()) {
        this._busy = true;
        this._shadeFrame(this.opts.camera, value)
          .finally(() => { this._busy = false; });
      } else {
        this._reshadeRequested = true; // pipeline busy — update() re-fires it
      }
    }
  }

  isVisible() {
    return this.splatMesh?.visible ?? false;
  }

  /** Compat shim — the old debug-mesh path built an OBJ ribbon mesh; the
   *  static-PLY pipeline doesn't have that input. No-op. */
  setDebugMesh(_visible) { /* not applicable to static-PLY pipeline */ }

  /** Add a 3D overlay to the scene so the user can visually inspect:
   *    - the env splat's axis-aligned bounding box (white wireframe)
   *    - the probe origin (red sphere) — where renderCubeMap captures from
   *    - the env splat centroid (yellow small sphere) — mean splat position
   *    - the hair centroid (green small sphere) — the head's location
   *  Useful for diagnosing why the probe captures a partial env. */
  showEnvDebug() {
    if (this._envDebug) return;
    const group = buildEnvDebugGroup({
      envMin: this._envMin,
      envMax: this._envMax,
      envCentroid: this._envCentroid,
      splatCentroid: this.data.centroid,
    });
    this.opts.scene.add(group);
    this._envDebug = group;
    console.info('[hair] env debug overlay shown — white box = env bounds, red = probe origin, green = head');
  }

  hideEnvDebug() {
    if (!this._envDebug) return;
    this.opts.scene.remove(this._envDebug);
    disposeEnvDebugGroup(this._envDebug);
    this._envDebug = null;
  }

  dispose() {
    try { this.runner?.dispose(); } catch {}
    try { this._gpuShader?.dispose(); } catch {}
    try { this._matUnpacker?.dispose(); } catch {}
    this._gpuShader = this._matUnpacker = null;
    if (this.splatMesh) {
      try { this.opts.scene.remove(this.splatMesh); } catch {}
      try { this.splatMesh.dispose?.(); } catch {}
    }
    this.runner = null;
    this.splatMesh = null;
  }
}
